import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Modal, ModalBody, ModalFooter } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'
import { PatientFormFields, usePatientForm } from '@/features/patient/PatientForm'
import { CallerFormFields, useCallerForm } from '@/features/callers/CallerForm'
import { ConvertCallerFields, useConvertCaller } from '@/features/callers/ConvertCallerForm'
import { TravelDayFields, useTravelDayForm } from '@/features/travel/TravelDayForm'
import {
  BookAppointmentFields,
  useBookAppointment,
} from '@/features/appointments/BookAppointmentForm'
import { ModalContext, useModals, type ModalApi, type ModalRequest } from './ModalContext'

/**
 * One dialog at a time, opened from anywhere.
 *
 * The forms below are mounted only while their dialog is on screen, so every
 * open starts clean — no stale half-filled registration from twenty minutes
 * ago. `seq` forces that even when the same kind is reopened back to back.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ModalRequest | null>(null)
  const [open, setOpen] = useState(false)
  const seq = useRef(0)

  const open_ = useCallback((next: ModalRequest) => {
    seq.current += 1
    setRequest(next)
    setOpen(true)
  }, [])
  const close = useCallback(() => setOpen(false), [])

  const api = useMemo<ModalApi>(() => ({ open: open_, close }), [open_, close])

  return (
    <ModalContext.Provider value={api}>
      {children}
      {request && (
        <Content key={seq.current} request={request} open={open} onClose={close} />
      )}
    </ModalContext.Provider>
  )
}

function Content({
  request,
  open,
  onClose,
}: {
  request: ModalRequest
  open: boolean
  onClose: () => void
}) {
  switch (request.kind) {
    case 'patient':
      return <PatientModal request={request} open={open} onClose={onClose} />
    case 'caller':
      return <CallerModal open={open} onClose={onClose} />
    case 'convert':
      return <ConvertModal callerId={request.callerId} open={open} onClose={onClose} />
    case 'travel':
      return <TravelModal open={open} onClose={onClose} />
    case 'appointment':
      return (
        <AppointmentModal
          date={request.date}
          patientId={request.patientId}
          open={open}
          onClose={onClose}
        />
      )
  }
}

function PatientModal({
  request,
  open,
  onClose,
}: {
  request: Extract<ModalRequest, { kind: 'patient' }>
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const modals = useModals()
  const editing = request.mode === 'edit'
  const state = usePatientForm({
    mode: request.mode,
    patientId: editing ? request.patientId : '',
    seed: editing ? undefined : request.seed,
    onSaved: (patient) => {
      onClose()
      if (editing) return
      const then = request.mode === 'create' ? request.then : undefined
      if (then) {
        // Came here mid-booking: go back with the new patient already chosen.
        modals.open({ kind: 'appointment', date: then.date, patientId: patient.id })
        return
      }
      // Otherwise registering is the start of a consultation, so land on the
      // record. An edit was made *from* the record, already behind the dialog.
      navigate(`/patients/${patient.id}`)
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit patient' : 'Register patient'}
      description={
        editing
          ? 'Identity only. Consultations, prescriptions and documents live on the record.'
          : 'An OP number and a name are enough to start. Everything else can follow.'
      }
      size="md"
    >
      {editing && state.loading ? (
        <ModalBody>
          <Spinner label="Loading patient" />
        </ModalBody>
      ) : (
        <form onSubmit={state.submit} className="flex min-h-0 flex-1 flex-col">
          <ModalBody>
            <div className="grid gap-5">
              {state.error && (
                <Banner tone="danger" live>
                  {state.error}
                </Banner>
              )}
              <PatientFormFields state={state} chrome="plain" autoFocus />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={state.save.isPending}>
              <Icon name="save" />
              {editing ? 'Save changes' : 'Register patient'}
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  )
}

function CallerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const state = useCallerForm({ mode: 'create', onSaved: onClose })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log a call"
      description="Only the name is required — fill the rest in after the phone goes down."
      size="md"
    >
      <form onSubmit={state.submit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <div className="grid gap-5">
            {state.error && <Banner tone="danger">{state.error}</Banner>}
            <CallerFormFields state={state} chrome="plain" autoFocus />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={state.save.isPending}>
            <Icon name="save" />
            Log call
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

function ConvertModal({
  callerId,
  open,
  onClose,
}: {
  callerId: string
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const state = useConvertCaller({
    callerId,
    onConverted: (result) => {
      onClose()
      navigate(`/patients/${result.patient.id}`)
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert to patient"
      description="Nothing already taken down on the call needs retyping."
      size="md"
    >
      {state.caller.isLoading ? (
        <ModalBody>
          <Spinner label="Loading enquiry" />
        </ModalBody>
      ) : (
        <form onSubmit={state.submit} className="flex min-h-0 flex-1 flex-col">
          <ModalBody>
            <div className="grid gap-5">
              {state.error && <Banner tone="danger">{state.error}</Banner>}
              <ConvertCallerFields state={state} chrome="plain" />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={state.convert.isPending}>
              <Icon name="userPlus" />
              Convert to patient
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  )
}

function TravelModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const state = useTravelDayForm({ onAdded: onClose })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add travel"
      description="Where the doctor sits, and for how long. A single day or a run of them."
      size="sm"
    >
      <form onSubmit={state.submit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <TravelDayFields state={state} />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={state.add.isPending}>
            <Icon name="plus" />
            Add travel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

function AppointmentModal({
  date,
  patientId,
  open,
  onClose,
}: {
  date?: string | undefined
  patientId?: string | undefined
  open: boolean
  onClose: () => void
}) {
  const modals = useModals()
  const state = useBookAppointment({ date, patientId, onBooked: onClose })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Book an appointment"
      description="Pick who is coming and when. The day says where you are sitting and how full it is."
      size="md"
    >
      <form onSubmit={state.submit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <BookAppointmentFields
            state={state}
            // Registering replaces this dialog and reopens it afterwards with
            // the new patient chosen, so the date typed so far is carried over.
            onRegisterNew={() => modals.open({ kind: 'patient', mode: 'create', then: { kind: 'appointment', date: state.on } })}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={state.save.isPending}>
            <Icon name="calendar" />
            Book appointment
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
